import { HttpErrorResponse } from '@angular/common/http';

import { ActionScopedPayload } from '../types';
import { pipeState } from '../utils';
import {
  BaseEntity,
  EntityCollectionAdapter,
  EntityState,
  ID,
  IdSelector,
  EntityPartial,
  Scope,
} from './types';
import { asActions, resolveScope } from './utils';

export type EntityUpdateOperationPayload<T extends BaseEntity> = EntityOperationAsyncPayload<
  EntityPartial<T>
>;

export type EntityOperationSyncPayload<T> = {
  data: T;
};

export type EntityOperationAsyncPayload<T> = {
  data: T;
  correlationId: string;
};

export const getEntityCollectionActions = <T extends BaseEntity, T1>(
  adapter: EntityCollectionAdapter<T>,
  selectId: IdSelector<T>,
) => {
  const config = {
    fetchCollection: (state: EntityState<T>, payload?: { scope: Scope }) =>
      adapter.markAsPending(state, { scope: resolveScope(payload?.scope), entities: [] }),
    fetchCollectionSuccess: (state: EntityState<T>, { data, scope }: ActionScopedPayload<T[]>) =>
      pipeState(
        state,
        next => adapter.setMany(next, data, { scope }),
        next =>
          adapter.markAsLoaded(next, {
            scope: resolveScope(scope),
            entities: data.map(e => selectId(e)),
          }),
      ),
    fetchCollectionFailed: (
      state: EntityState<T>,
      payload: { error: HttpErrorResponse; scope?: Scope },
    ) =>
      pipeState(
        state,
        next => adapter.markAsLoaded(next, { scope: resolveScope(payload.scope), entities: [] }),
        next =>
          adapter.setError(next, payload.error, {
            scope: resolveScope(payload.scope),
            entities: [],
          }),
      ),
    createOne: (
      state: EntityState<T>,
      payload: EntityOperationSyncPayload<T> & ActionScopedPayload<T>,
    ) =>
      pipeState(
        state,
        next =>
          adapter.markAsLoaded(next, {
            entities: [selectId(payload.data)],
            scope: null,
          }),
        next =>
          adapter.addOne(next, payload.data, {
            scope: payload.scope,
          }),
      ),
    createOneOptimistic: (
      state: EntityState<T>,
      payload: EntityOperationAsyncPayload<Partial<T>> & ActionScopedPayload<Partial<T>>,
    ) =>
      pipeState(
        state,
        next =>
          adapter.markAsPending(next, {
            entities: [payload.correlationId],
            scope: null,
          }),
        next =>
          adapter.addOne(next, <T>payload.data, {
            scope: payload.scope,
            correlationId: payload.correlationId,
          }),
      ),

    createOneOptimisticSuccess: (
      state: EntityState<T>,
      payload: EntityOperationAsyncPayload<T> & ActionScopedPayload<T>,
    ) =>
      pipeState(
        state,
        next => adapter.replaceOne(next, payload.correlationId, payload.data),
        next =>
          adapter.markAsLoaded(next, {
            entities: [selectId(payload.data)],
            scope: null,
          }),
        next =>
          adapter.setAlias(next, {
            [payload.correlationId]: selectId(payload.data),
          }),
      ),

    createOneOptimisticFailed: (
      state: EntityState<T>,
      payload: EntityOperationAsyncPayload<T> &
        ActionScopedPayload<T> & { error: HttpErrorResponse },
    ) =>
      pipeState(
        state,
        next => adapter.removeOne(next, payload.correlationId),
        next =>
          adapter.setError(next, payload.error, { scope: null, entities: [payload.correlationId] }),
      ),

    createOnePessimistic: (
      state: EntityState<T>,
      payload:
        | (EntityOperationAsyncPayload<T> & ActionScopedPayload<T>)
        | (EntityOperationAsyncPayload<T1> & ActionScopedPayload<T1>),
    ) => {
      const id = payload.correlationId || selectId(<T>payload.data);

      if (!id) {
        throw new Error('key of entity could not be determined');
      }

      return adapter.markAsPending(state, {
        entities: [id],
        scope: null,
      });
    },

    createOnePessimisticSuccess: (
      state: EntityState<T>,
      payload: EntityOperationAsyncPayload<T> & ActionScopedPayload<T>,
    ) => {
      const id = selectId(payload.data);

      return pipeState(
        state,
        next =>
          adapter.markAsLoaded(next, {
            entities: [id],
            scope: null,
          }),
        next =>
          payload.correlationId
            ? pipeState(
                next,
                next => adapter.removeOne(next, payload.correlationId),
                next =>
                  adapter.setAlias(next, {
                    [payload.correlationId]: id,
                  }),
              )
            : next,
        next => adapter.addOne(next, { ...payload.data }, { scope: payload.scope }),
      );
    },

    createOnePessimisticFailed: (
      state: EntityState<T>,
      payload: EntityOperationAsyncPayload<T> &
        ActionScopedPayload<T> & { error: HttpErrorResponse },
    ) => adapter.setError(state, payload.error, { entities: [payload.correlationId], scope: null }),

    updateOne: (state: EntityState<T>, payload: EntityOperationSyncPayload<T>) =>
      adapter.updateOne(state, payload.data),

    updateOneOptimistic: (state: EntityState<T>, payload: EntityOperationAsyncPayload<T>) =>
      pipeState(
        state,
        next =>
          adapter.optimisticUpdateSet(next, payload.correlationId, {
            before: state.entityMap[selectId(payload.data)]!,
            update: payload.data,
          }),
        next => adapter.updateOne(next, payload.data),
        next =>
          adapter.markAsPending(next, {
            entities: [selectId(payload.data)],
            scope: null,
          }),
      ),
    updateOneOptimisticSuccess: (
      state: EntityState<T>,
      { data, correlationId }: { data: T } & EntityUpdateOperationPayload<T>,
    ) =>
      pipeState(
        state,
        next => adapter.markAsLoaded(next, { entities: [selectId(data)], scope: null }),
        next => adapter.optimisticUpdateRemove(next, correlationId),
        next => adapter.updateOne(next, data),
      ),
    updateOneOptimisticFailed: (
      state: EntityState<T>,
      {
        data,
        correlationId,
        error,
      }: EntityUpdateOperationPayload<T> & { error: HttpErrorResponse },
    ) => {
      const transaction = state.optimisticUpdate[correlationId];

      const rollbackUpdate = <EntityPartial<T>>(
        Object.fromEntries(
          Object.keys(transaction.update).map(updatedKey => [
            updatedKey,
            transaction.before[<keyof T>updatedKey],
          ]),
        )
      );

      return pipeState(
        state,
        next => adapter.optimisticUpdateRemove(next, correlationId),
        next =>
          adapter.setError(next, error, { entities: [selectId(transaction.before)], scope: null }),
        next => adapter.updateOne(next, rollbackUpdate),
      );
    },
    updateOnePessimistic: (state: EntityState<T>, payload: { data: T }) =>
      adapter.markAsPending(state, {
        entities: [selectId(payload.data)],
        scope: null,
      }),
    updateOnePessimisticSuccess: (state: EntityState<T>, { data }: { data: T }) =>
      pipeState(
        state,
        next => adapter.markAsLoaded(next, { entities: [selectId(data)], scope: null }),
        next => adapter.updateOne(next, data),
      ),
    updateOnePessimisticFailed: (
      state: EntityState<T>,
      { data, error }: { data: T; error: HttpErrorResponse },
    ) => adapter.setError(state, error, { entities: [selectId(data)], scope: null }),

    removeOne: (
      state: EntityState<T>,
      payload: EntityOperationSyncPayload<ID> & ActionScopedPayload<ID>,
    ) => adapter.removeOne(state, payload.data),

    removeOneOptimistic: (state: EntityState<T>, payload: { data: ID }) => {
      const before = state.entityMap[payload.data]!;

      return pipeState(
        state,
        next => adapter.markAsPending(next, { entities: [payload.data], scope: null }),
        next => adapter.optimisticDeleteSet(next, before),
        next => adapter.removeOne(next, payload.data),
      );
    },
    removeOneOptimisticSuccess: (state: EntityState<T>, { data: id }: { data: ID }) =>
      pipeState(state, next => adapter.optimisticDeleteRemove(next, id)),
    removeOneOptimisticFailed: (
      state: EntityState<T>,
      { data: id, error }: { data: ID } & { error: HttpErrorResponse },
    ) => {
      const entity = state.optimisticDelete[id];

      return pipeState(
        state,
        next => adapter.optimisticDeleteRemove(next, id),
        next => adapter.setError(next, error, { entities: [id], scope: null }),
        next => adapter.addOne(next, entity),
      );
    },

    removeOnePessimistic: (state: EntityState<T>, payload: { data: ID }) =>
      adapter.markAsPending(state, { entities: [payload.data], scope: null }),
    removeOnePessimisticSuccess: (state: EntityState<T>, { data }: { data: ID }) =>
      adapter.removeOne(state, data),
    removeOnePessimisticFailed: (
      state: EntityState<T>,
      { data: id, error }: { data: ID } & { error: HttpErrorResponse },
    ) => adapter.setError(state, error, { entities: [id], scope: null }),
  };

  return asActions<T, typeof config>(config);
};
